import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelYearTabs } from "@/components/fuel-activity/FuelYearPicker";
import { YearlySpendBreakdownView } from "@/components/fuel-activity/YearlySpendBreakdownView";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { getAvailableFuelYears, getYearlySpendBreakdown } from "@/lib/fuel-analytics";
import { useFuelActivityData } from "@/lib/queries";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function FuelSpendYearPage() {
  const { year: yearParam } = useParams();
  const year = Number(yearParam);
  const { data, isLoading, error } = useFuelActivityData();

  const availableYears = useMemo(() => (data ? getAvailableFuelYears(data.logs) : []), [data]);

  const breakdown = useMemo(() => {
    if (!data || !Number.isFinite(year)) return null;
    return getYearlySpendBreakdown(data.logs, data.users, data.cars, data.stations, year);
  }, [data, year]);

  if (!Number.isFinite(year)) {
    return <Navigate to="/fuel-activity/spend" replace />;
  }

  if (!isLoading && data && availableYears.length > 0 && !availableYears.includes(year)) {
    return <Navigate to={`/fuel-activity/spend/${availableYears[0]}`} replace />;
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <Link
        to="/fuel-activity/spend"
        className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
      >
        ← All years
      </Link>

      <PageHeader
        title={`${year} spend breakdown`}
        description={
          breakdown?.isCurrentYear
            ? "Year-to-date fuel spend across all users."
            : `Full-year fuel spend across all users for ${year}.`
        }
        showGreeting={false}
      />

      <FuelMenuNav />

      {availableYears.length > 0 ? (
        <FuelYearTabs years={availableYears} activeYear={year} basePath="/fuel-activity/spend" />
      ) : null}

      {isLoading ? (
        <ExplorerSkeleton title={`${year} spend`} />
      ) : error || !data || !breakdown ? (
        <p className="text-sm text-red-600">Failed to load spend breakdown for {year}.</p>
      ) : (
        <YearlySpendBreakdownView breakdown={breakdown} scope="platform" basePath="/fuel-activity/spend" />
      )}
    </motion.div>
  );
}
